import Table from 'components/common/Table/Table';
import DataLoader from 'components/UI/DataLoader/DataLoader';
import { AnimatedEllipsis } from 'components/AnimatedEllipsis';
import { MuiBox } from 'theme/material-ui';
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { currentAccountConsumerSelector } from 'store/account/selectors';
import { RootState, RootDispatch } from 'store/types';
import { getVitalsForLastTwoYears, setHistoricalVitalType } from 'store/vitals/actions';
import * as vitalsSelectors from 'store/vitals/selectors';
import { StyledScrollableLayout, BackgroundWrapper } from './styled';
import { useParams } from 'react-router-dom';
import { oc } from 'ts-optchain';
import VitalsChart from 'components/vitals/VitalsChart/VitalsChart';
import Banner from 'screens/Vitals/Banner';
import { HistoricalVitalsDataObject } from 'store/vitals/types';
import { HEALTH_RECORDS_PAGES } from 'lib/constants/healthRecords';
import { BLOOD_PRESSURE_VITAL_SERVICE_NAME } from 'store/vitals/constants';
import { HealthRecordsListItemPlaceholder } from '../HealthRecords/shared';

interface Props {
  isFetching: boolean;
  dispatch: RootDispatch;
  data: HistoricalVitalsDataObject[];
  error: Error | null;
}

export const VitalsTable = ({ dispatch, data, isFetching = false, error }: Props) => {
  const { name } = useParams();
  const [isWaiting, setIsWaiting] = useState(true);

  // There is a race condition that arises from the historical data not being reset IF you navigate from the health record home page

  useEffect(() => {
    if (isWaiting) {
      setTimeout(() => setIsWaiting(false));
    }
  }, [isWaiting]);

  const consumer = useSelector(currentAccountConsumerSelector);
  const patientName = oc(consumer).displayName('');
  const age = oc(consumer).age();
  const shouldShowSecondaryValue = () => name === BLOOD_PRESSURE_VITAL_SERVICE_NAME;

  useEffect(() => {
    dispatch(setHistoricalVitalType(name));
    dispatch(getVitalsForLastTwoYears());
  }, [name]);

  const renderLoadingState = () => (
    <>
      <HealthRecordsListItemPlaceholder message={HEALTH_RECORDS_PAGES.Vitals.loading} />
      <MuiBox display="flex" flexDirection="row" justifyContent="center">
        <AnimatedEllipsis />
      </MuiBox>
    </>
  );

  const renderErrorState = () => (
    <HealthRecordsListItemPlaceholder message={HEALTH_RECORDS_PAGES.Vitals.getErrorState(name)} />
  );

  const renderData = () => (
    <StyledScrollableLayout>
      <Banner patientName={patientName} patientAge={age} />
      <BackgroundWrapper>
        <VitalsChart data={data} type={name} showSecondaryValue={shouldShowSecondaryValue()} />
      </BackgroundWrapper>
      <Table data={data} type={name} isFetching={isFetching} />
    </StyledScrollableLayout>
  );

  return (
    <DataLoader
      error={error}
      loading={isFetching || isWaiting}
      renderData={renderData}
      renderError={renderErrorState}
      renderLoading={renderLoadingState}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  data: vitalsSelectors.historicalVitalsChartDataSelector(state),
  isFetching: vitalsSelectors.recentVitalsLoadingSelector(state),
  error: vitalsSelectors.recentVitalsErrorSelector(state)
});

export default connect(mapStateToProps)(VitalsTable);
